<template lang="pug">
.dashboard-wrapper
  b-row
    b-col
      b-card(no-body, class="pt-2 px-2")
        filters(
          :dateRangeFilterShow="true"
          @changeFilter="updateFilter"
        )

  b-row(v-if="isLoading")
    b-col
      b-card(class="py-5")
        loading(
          :active="true"
          :is-full-page="false"
          :color="colors.primary"
        )

  div(v-else)
    //- Adherence by question type
    b-row
      b-col(cols="12")
        b-card
          b-card-title(class="p-2 mb-0 text-center")
            | {{ $t('Adherence by Question Type') }}

          b-table-simple(hover, small, responsive, class="text-center")
            b-thead(head-variant="light")
              b-tr
                th {{ $t('message.tableHeader.question_type') }}
                th {{ $t('message.tableHeader.score') }}
                th {{ $t('message.tableHeader.total') }}
                th {{ $t('message.tableHeader.adherence') }}
            b-tbody
              b-tr(v-for="row in adherenceRows", :key="row.id")
                b-td {{ row.name }}
                b-td {{ row.noData ? '-' : row.score }}
                b-td {{ row.noData ? '-' : row.total }}
                b-td(:class="row.noData ? '' : getComplianceClass(row.adherence)")
                  span(v-if="row.noData") {{ $t('message.no_data') }}
                  span(v-else) {{ row.adherenceText }}
              b-tr(v-if="adherenceHasData")
                b-td(class="font-weight-bold")
                  | {{ $t('message.tableHeader.total') }}
                b-td(class="font-weight-bold")
                  | {{ adherenceTotals.score }}
                b-td(class="font-weight-bold")
                  | {{ adherenceTotals.total }}
                b-td(:class="getComplianceClass(adherenceTotals.adherence)")
                  | {{ adherenceTotals.adherence }}%
              b-tr(v-if="!adherenceRows.length")
                b-td(colspan="4", class="text-center")
                  | {{ $t('message.no_records') }}

    //- Top 10 workers by question type
    b-row
      b-col(cols="12")
        b-card
          b-card-title(class="p-2 mb-0 text-center")
            | {{ $t('Top 10 by Question Type') }}

          b-row(class="justify-content-center")
            b-col(md="4")
              b-form-group(:label="$t('label.question_type')", label-for="question-type-filter")
                v-select#question-type-filter(
                  v-model="selectedQuestionType",
                  :reduce="(types) => types.value",
                  label="title",
                  :options="questionTypeOptions",
                  :placeholder="$t('placeholder.select_question_type')"
                )

          b-table-simple(hover, small, responsive, class="text-center")
            b-thead(head-variant="light")
              b-tr
                th {{ $t('message.tableHeader.position') }}
                th {{ $t('message.tableHeader.worker') }}
                th {{ $t('message.tableHeader.score') }}
                th {{ $t('message.tableHeader.total') }}
                th {{ $t('message.tableHeader.adherence') }}
            b-tbody
              b-tr(v-for="row in topWorkers", :key="row.id")
                b-td {{ row.position }}°
                b-td {{ row.name }}
                b-td {{ row.score }}
                b-td {{ row.total }}
                b-td(:class="getComplianceClass(row.adherence)")
                  | {{ row.adherenceText }}
              b-tr(v-if="!topWorkers.length")
                b-td(colspan="5", class="text-center")
                  | {{ $t('message.no_data') }}

</template>

<script>
import { onMounted, ref, computed } from '@vue/composition-api/dist/vue-composition-api'
import { BCard, BCardTitle, BFormGroup, BTableSimple, BThead, BTbody, BTr, BTd } from 'bootstrap-vue'
import vSelect from 'vue-select'
import Filters from '@/views/organization/Filters.vue'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import store from '@/store'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date, idToString } from '@/helpers/mongoHelpers'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import useNotifications from '@/composables/useNotifications'
import { colors } from '@/constants'

export default {
  components: {
    BCard,
    BCardTitle,
    BFormGroup,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTd,
    vSelect,
    Filters,
    Loading,
  },
  setup() {
    const { showErrorMessage } = useNotifications()
    const { getItems, getItemsAggregate } = useLambda()
    const { processAdherence, getDatesFromRange } = useCommonDashboards()
    const userData = store.state?.userStore?.userData
    const clientId = userData.role !== 'admin' ? userData.client.$oid : null
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    const lastDayOfMonthNumber = new Date(currentYear, currentMonth + 1, 0).getDate()
    const dateRangeFilter = ref(`01-${currentMonth + 1}-${currentYear} to ${lastDayOfMonthNumber}-${currentMonth + 1}-${currentYear}`)
    const isLoading = ref(true)
    const questionTypes = ref([])
    const processesData = ref([])
    const confirmationsData = ref([])
    const selectedQuestionType = ref('')

    const isScoreableBehaviour = behaviour => ['bool', 'rating'].includes(behaviour.type)
      && behaviour.answer
      && behaviour.answer !== 'unknown'
      && behaviour.answer !== 'N/A'

    const applyScore = (stats, behaviour) => {
      if (behaviour.type === 'bool') {
        if (behaviour.answer === 'true') stats.score += 1
        if (behaviour.answer === 'true' || behaviour.answer === 'false') stats.total += 1
      } else {
        const value = parseInt(behaviour.answer)
        if (Number.isNaN(value)) return
        stats.score += value
        stats.total += 5
      }
    }

    const processesById = computed(() => new Map(processesData.value.map(process => [idToString(process._id), process])))

    const resolveBehaviourTypeIds = (confirmation, activity, behaviour, behaviourIndex) => {
      const ownTypeIds = (behaviour.question_type || []).map(idToString)
      if (ownTypeIds.length) return ownTypeIds

      const processActivities = processesById.value.get(idToString(confirmation.process))?.activities
      const definitionBehaviour = processActivities?.find(definitionActivity => definitionActivity.name === activity.name)?.behaviours?.[behaviourIndex]
      return (definitionBehaviour?.question_type || []).map(idToString)
    }

    const fetchData = async () => {
      isLoading.value = true

      try {
        const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

        const types = await getItems({ collection: 'question_type', query: { client_id: oid(clientId), deleted: { $ne: true } } }) || []
        const processes = await getItems({ collection: 'process', query: { client_id: oid(clientId), deleted: { $ne: true } } }) || []
        const confirmations = await getItemsAggregate({
          collection: 'confirmation',
          pipeline: [
            { $match: { client_id: oid(clientId), date: { $gte: date(startFilter), $lt: date(endFilter) }, pending: { $ne: true } } },
            { $lookup: { from: 'worker', localField: 'worker', foreignField: '_id', pipeline: [ { $project: { name: 1 } } ], as: 'worker' } },
            { $addFields: { worker: { $arrayElemAt: ['$worker', 0] } } },
            { $project: { activities: 1, worker: 1 } },
          ],
        }) || []

        questionTypes.value = types
        processesData.value = processes
        confirmationsData.value = confirmations

        const validIds = new Set(types.map(type => idToString(type._id)))
        if (!validIds.has(selectedQuestionType.value)) selectedQuestionType.value = validIds.size ? idToString(types[0]._id) : ''
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.question_type_report_fetch_error'))
      } finally {
        isLoading.value = false
      }
    }

    const updateFilter = data => {
      dateRangeFilter.value = data.dateRangeFilter
      fetchData()
    }

    const questionTypeOptions = computed(() => questionTypes.value.map(type => ({ title: type.name, value: idToString(type._id) })))

    const adherenceRows = computed(() => {
      const typesById = new Map(questionTypes.value.map(type => [idToString(type._id), type]))

      const usedTypeIds = new Set()
      processesData.value.forEach(process => {
        process.activities?.forEach(activity => {
          activity.behaviours?.forEach(behaviour => {
            (behaviour.question_type || []).forEach(q => {
              const typeId = idToString(q)
              if (typesById.has(typeId)) usedTypeIds.add(typeId)
            })
          })
        })
      })

      const stats = new Map([...usedTypeIds].map(typeId => [typeId, { score: 0, total: 0 }]))

      confirmationsData.value.forEach(confirmation => {
        confirmation.activities?.forEach(activity => {
          activity.behaviours?.forEach((behaviour, behaviourIndex) => {
            if (!isScoreableBehaviour(behaviour)) return
            resolveBehaviourTypeIds(confirmation, activity, behaviour, behaviourIndex).forEach(typeId => {
              const typeStats = stats.get(typeId)
              if (!typeStats) return
              applyScore(typeStats, behaviour)
            })
          })
        })
      })

          return questionTypes.value
            .filter(type => usedTypeIds.has(idToString(type._id)))
            .map(type => {
              const typeId = idToString(type._id)
              const typeStats = stats.get(typeId)
              if (!typeStats || !typeStats.total) return { id: typeId, name: type.name, noData: true, score: '-', total: '-', adherence: 0, adherenceText: '' }
              const adherence = processAdherence(typeStats.score, typeStats.total)
              return { id: typeId, name: type.name, noData: false, score: typeStats.score, total: typeStats.total, adherence, adherenceText: `${adherence}%` }
            })
            .sort((a, b) => {
              if (a.noData !== b.noData) return a.noData ? 1 : -1
              if (a.noData) return a.name.localeCompare(b.name)
              return b.adherence - a.adherence || b.total - a.total || a.name.localeCompare(b.name)
            })
    })

    const adherenceHasData = computed(() => adherenceRows.value.some(row => !row.noData))
    const adherenceTotals = computed(() => {
      let score = 0
      let total = 0
      adherenceRows.value.forEach(row => {
        if (row.noData) return
        score += row.score
        total += row.total
      })
      return { score, total, adherence: processAdherence(score, total) }
    })

    const topWorkers = computed(() => {
      const typeId = selectedQuestionType.value
      if (!typeId) return []

      const workerStats = new Map()
      confirmationsData.value.forEach(confirmation => {
        const workerId = idToString(confirmation.worker?._id)
        const workerName = confirmation.worker?.name
        if (!workerId || !workerName) return

        confirmation.activities?.forEach(activity => {
          activity.behaviours?.forEach((behaviour, behaviourIndex) => {
            if (!isScoreableBehaviour(behaviour)) return
            if (!resolveBehaviourTypeIds(confirmation, activity, behaviour, behaviourIndex).includes(typeId)) return

            const stats = workerStats.get(workerId) || { name: workerName, score: 0, total: 0 }
            applyScore(stats, behaviour)
            workerStats.set(workerId, stats)
          })
        })
      })

      return [...workerStats.entries()]
        .map(([id, stats]) => ({ id, name: stats.name, score: stats.score, total: stats.total, adherence: processAdherence(stats.score, stats.total) }))
        .filter(stats => stats.total > 0)
        .sort((a, b) => b.adherence - a.adherence || b.total - a.total)
        .slice(0, 10)
        .map((stats, index) => ({ ...stats, position: index + 1, adherenceText: `${stats.adherence}%` }))
    })

    const getComplianceClass = value => {
      if (value >= 90) return 'compliance-dark-green'
      if (value >= 70) return 'compliance-green'
      if (value >= 50) return 'compliance-yellow'
      if (value >= 25) return 'compliance-orange'
      return 'compliance-red'
    }

    onMounted(() => {
      fetchData()
    })

    return {
      colors,
      isLoading,
      selectedQuestionType,
      questionTypeOptions,
      adherenceRows,
      adherenceHasData,
      adherenceTotals,
      topWorkers,
      updateFilter,
      getComplianceClass,
    }
  },
}
</script>

<style scoped>
.compliance-dark-green {
  background-color: #006400;
  color: white;
}

.compliance-green {
  background-color: #00cc44;
  color: white;
}

.compliance-yellow {
  background-color: #ffcc00;
  color: black;
}

.compliance-orange {
  background-color: #ff8000;
  color: white;
}

.compliance-red {
  background-color: #cc0000;
  color: white;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
